import {CoreModalConfirm} from '#components';

const modals = ref([]);

class Modal {
  constructor({
    id = Date.now(),
    size = 'md',
    bgClass = 'bg-white',
    backdropClass = 'bg-black/50',
    overflowClass = 'overflow-hidden',
    roundedClass = 'rounded',
    component,
    props,
    eventHandlers,
    fullHeight,
    showClose = true,
    closeButtonProps = {
      color: 'gray',
      variant: 'ghost'
    },
    clickToClose = true,
    escToClose = true,
    onClose = () => {},
    onOpen = () => {}
  } = {}) {
    this.id = id;
    this.size = size;
    this.bgClass = bgClass;
    this.backdropClass = backdropClass;
    this.overflowClass = overflowClass;
    this.roundedClass = roundedClass;
    // Let the modal inherit the available height from the fixed wrapper (which already
    // applies inset + padding) instead of recalculating viewport height with vh units.
    this.fullHeightClass = fullHeight ? ' flex flex-col min-h-0 overflow-hidden max-h-full' : '';
    this.showClose = showClose;
    this.closeButtonProps = closeButtonProps;
    this.clickToClose = clickToClose;
    this.escToClose = escToClose;
    this.component = shallowRef(component);
    this.props = props;
    this.onClose = onClose;
    this.onOpen = onOpen;
    this.eventHandlers = Object.entries(eventHandlers || {})
      .reduce((boundHandlers, [eventName, handler]) => {
        if (typeof handler === 'function') {
          boundHandlers[eventName] = handler.bind(this);
        }
        return boundHandlers;
      }, {});
    this.isOpen = false;

    this.isFullScreen = (size === 'fullscreen');

    this.sizeClass = computed(() => {
      switch (size) {
        case 'xs':
          return 'w-[250px]';
        case 'sm':
          return 'w-[400px]';
        case 'sm-md':
          return 'w-[550px]';
        case 'md':
          return 'w-[772px]';
        case 'lg':
          return 'w-[1000px]';
        case 'xl':
          return 'w-[1200px]';
        case '2xl':
          return 'w-[1600px]';
        case 'fullscreen':
          return 'w-screen h-dvh md:h-screen';
      }
    });
  }

  async open() {
    if (modals.value.some(item => item.id === this.id)) {
      return this;
    }

    modals.value.push(this);
    await nextTick();
    this.isOpen = true;
    this.onOpen();
    return this;
  }

  async close() {
    this.isOpen = false;
    await nextTick();
    await waitFor(250); //this is a magic number, whatev
    modals.value = modals.value.filter(item => item.id !== this.id);
    this.onClose();
  }

}

export function useFModal(args) {
   return reactive(new Modal(args));
}

export function useConfirmModal({heading, confirmMethod, confirmText, color = 'red', message, warning, size = 'sm', onConfirm, nonCancelable}) {
  return reactive(new Modal({
    size,
    showClose: false,
    component: CoreModalConfirm,
    clickToClose: !nonCancelable,
    props: {
      color,
      heading,
      confirmMethod,
      confirmText,
      message,
      warning,
      nonCancelable
    },
    eventHandlers: {
      onConfirm
    }
  }));
}

export function useFModals() {

  function clear() {
    modals.value.length = 0;
  }

  function closeTopModal() {
    modals.value.pop();
  }

  function getTopModal() {
    return modals.value[modals.value.length - 1];
  }

  return {
    modals,
    clear,
    closeTopModal,
    getTopModal,
    areModalsOpen: computed(() => modals.value.length > 0)
  };
}
